import type { PaginationSearchParams } from '@shared/api/types/common';
import { captchaDecorator } from '@shared/helpers/captcha';

import type { JsonApiObject } from '../helpers/jsonapi';
import { deserialize } from '../helpers/jsonapi';
import type { ApiOptions } from './ApiProvider';
import Api from './ApiProvider';
import type {
  BinanceCheckoutEntry,
  CanClaimOfferEntry,
  ClaimPowOfferResponse,
  DeserializedHotOffer,
  HotOffersEntry,
  PartnerStatsData,
} from './types/hotOffers';

export const getHotOffer = async (offerId: string, options?: ApiOptions) => {
  const rawApp = await Api.get(
    `v2/hot-offers/how-to-get/${offerId}`,
    options,
  ).json<JsonApiObject>();

  return deserialize<DeserializedHotOffer>(rawApp);
};

export const getHotToGetOffer = async (
  options: ApiOptions<{
    searchParams: {
      offerId: string;
    };
  }>,
) => {
  const rawApp = await Api.get(
    `v2/hot-offers/how-to-get/${options.searchParams.offerId}`,
  ).json<JsonApiObject>();

  return deserialize<DeserializedHotOffer>(rawApp);
};

export const HOT_OFFERS_FILTER = {
  ALL: 'all',
  NEW: 'new',
  UNCLAIMED: 'unclaimed',
  CLAIMED: 'claimed',
} as const;

export const GET_HOT_OFFERS_DEFAULT_LIMIT = 12;

export const GET_HOT_OFFERS_DEFAULT_FILTER = HOT_OFFERS_FILTER.ALL;

type HotOffersFilterName = keyof typeof HOT_OFFERS_FILTER;

export type HotOffersFilterValue = (typeof HOT_OFFERS_FILTER)[HotOffersFilterName];

export type GetHotOffersSearchParams = {
  limit?: number;
  offset?: number;
  mode?: HotOffersFilterValue;
};

export const isGetHotOffersFilterParam = (value: unknown): value is HotOffersFilterValue => {
  return (
    !!value &&
    typeof value === 'string' &&
    Object.values(HOT_OFFERS_FILTER).includes(value as HotOffersFilterValue)
  );
};

export const getHotOffersSearchParams = (searchParams?: {
  mode?: string;
}): Required<Pick<GetHotOffersSearchParams, 'mode'>> => {
  const mode = searchParams?.mode?.toLowerCase();

  if (!mode) {
    return {
      mode: GET_HOT_OFFERS_DEFAULT_FILTER,
    };
  }

  return {
    mode: isGetHotOffersFilterParam(mode) ? mode : GET_HOT_OFFERS_DEFAULT_FILTER,
  };
};

export const getHotOffers = (
  options?: ApiOptions<{
    searchParams: GetHotOffersSearchParams;
  }>,
) => {
  const optionsWithDefaults = {
    ...options,
    searchParams: {
      limit: GET_HOT_OFFERS_DEFAULT_LIMIT,
      mode: GET_HOT_OFFERS_DEFAULT_FILTER,
      ...options?.searchParams,
    },
  };

  return Api.get('v2/hot-offers/list', optionsWithDefaults).json<HotOffersEntry>();
};

export const getCanClaimOffer = (offerId: string, options?: ApiOptions) =>
  Api.get(`v2/hot-offers/how-to-get/${offerId}/allowance`, options).json<CanClaimOfferEntry>();

export const createBinancePay = captchaDecorator<{ searchParams: { offerId: string } }>(
  async (options) =>
    Api.post('hot-offers/cashback/create-order', options).json<BinanceCheckoutEntry>(),
  'createBinancePay',
);

export const getOfferClaimUrl = (offerId: string, options?: ApiOptions) =>
  Api.post(`v2/hot-offers/general/${offerId}/claim`, options).json<{
    id: string;
    type: string;
    attributes: {
      url: string;
    };
  }>();

export const getPowOfferClaim = (options: ApiOptions<{ json: { offerId: string } }>) => {
  const { offerId } = options.json;

  return Api.post(`v2/hot-offers/pow/${offerId}/claim`, options).json<ClaimPowOfferResponse>();
};

export const getHotOfferShareLink = (offerId: string, options?: ApiOptions) =>
  Api.get(`v2/store/hot-offers/${offerId}/share-link`, options).json<{ sharingLink: string }>();

export interface AddPowOfferProofsParams {
  fileUuids?: string[];
  link?: string;
  text?: string;
  offerId?: string;
}

export const addPowOfferProof = (options: ApiOptions<{ json: AddPowOfferProofsParams }>) => {
  const { offerId } = options.json;

  const otherOptions = options;
  delete otherOptions.json.offerId;

  return Api.post(`v2/hot-offers/pow/${offerId}/claim/proof`, otherOptions).json<{
    status: string;
  }>();
};

export const getHotOfferPartnerStats = async (
  options?: ApiOptions<{ searchParams?: PaginationSearchParams }>,
) => {
  const data = await Api.get('v2/hot-offers/partner-stats', options).json<JsonApiObject>();

  return deserialize<PartnerStatsData[]>(data);
};
